import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useVirtualizer } from '@tanstack/react-virtual';
import { AlertCircle, Keyboard, MousePointer2, Navigation, Scroll, Type } from 'lucide-react';
import type { DetrixActionLine, DetrixSession } from '../../types';
import { usePlaybackStore } from '../../store/usePlaybackStore';
import { useSessionUiStore } from '../../store/useSessionUiStore';
import { formatSessionOffset } from '../../utils/time';
import { getErrorCorrelation, isSelectionError } from '../../utils/sessionErrorSignals';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const KindIcon = ({ kind }: { kind: DetrixActionLine['kind'] }) => {
  switch (kind) {
    case 'click':
      return <MousePointer2 className="h-3.5 w-3.5" />;
    case 'keydown':
      return <Keyboard className="h-3.5 w-3.5" />;
    case 'input':
      return <Type className="h-3.5 w-3.5" />;
    case 'scroll':
      return <Scroll className="h-3.5 w-3.5" />;
    case 'navigate':
      return <Navigation className="h-3.5 w-3.5" />;
    case 'error':
      return <AlertCircle className="h-3.5 w-3.5 text-[var(--error)]" />;
    default:
      return null;
  }
};

export const ActionsPanel: React.FC<{ session: DetrixSession }> = ({ session }) => {
  const { actions = [] } = session;
  const { focusedTimestamp, isPlaying } = usePlaybackStore();
  const { selectedId, setSelectedId, activeTab } = useSessionUiStore();
  const parentRef = useRef<HTMLDivElement>(null);

  const correlation = useMemo(() => {
    if (!selectedId || !isSelectionError(session, activeTab, selectedId)) return null;
    return getErrorCorrelation(session, activeTab, selectedId);
  }, [session, activeTab, selectedId]);

  const playheadIndex = useMemo(() => {
    if (focusedTimestamp == null || actions.length === 0) return -1;
    let lo = -1;
    for (let i = 0; i < actions.length; i++) {
      const ts = actions[i]!.timestamp;
      if (ts <= focusedTimestamp) lo = i;
      else break;
    }
    return lo;
  }, [actions, focusedTimestamp]);

  const virtualizer = useVirtualizer({
    count: actions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 32,
    overscan: 10,
  });

  useLayoutEffect(() => {
    if (playheadIndex < 0) return;
    virtualizer.scrollToIndex(playheadIndex, { align: 'center' });
  }, [focusedTimestamp, playheadIndex, virtualizer]);

  useLayoutEffect(() => {
    if (!isPlaying || playheadIndex < 0) return;
    const id = actions[playheadIndex]!.id;
    if (id === selectedId) return;
    setSelectedId(id);
  }, [actions, isPlaying, playheadIndex, selectedId, setSelectedId]);

  useLayoutEffect(() => {
    if (!selectedId) return;
    const idx = actions.findIndex((a) => a.id === selectedId);
    if (idx < 0) return;
    if (actions[idx]?.kind === 'error') {
      virtualizer.scrollToIndex(idx, { align: 'center' });
    }
  }, [selectedId, actions, virtualizer]);

  if (actions.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center text-[var(--text-muted)]">
        <MousePointer2 className="mb-2 h-8 w-8 opacity-20" />
        <p>No granular actions captured.</p>
      </div>
    );
  }

  return (
    <div ref={parentRef} className="min-h-0 flex-1 overflow-y-auto">
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const action = actions[virtualRow.index]!;
          const isSelected = selectedId === action.id;
          const isAtPlayhead = playheadIndex === virtualRow.index;
          const isCorr = correlation?.prevActionId === action.id;
          const errorSelected = isSelected && action.kind === 'error';

          return (
            <div
              key={action.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelectedId(action.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedId(action.id);
                }
              }}
              className={cn(
                'absolute left-0 top-0 z-0 flex w-full cursor-pointer items-center gap-3 border-b border-[var(--border-subtle)] px-4 text-[12px] transition-colors',
                isSelected && !errorSelected ? 'z-10 bg-[var(--list-row-selected-bg)] outline-1 outline-[var(--accent)]' : '',
                errorSelected ? 'z-10 bg-[var(--list-row-error-bg)] outline outline-1 outline-[var(--list-row-error-ring)]' : '',
                !isSelected ? 'hover:bg-[var(--bg-elevated)]' : '',
                isAtPlayhead && !isSelected ? 'bg-[var(--accent)]/8 ring-1 ring-[var(--accent)]/30' : '',
                isCorr ? 'z-[8] bg-amber-950/25 ring-1 ring-inset ring-amber-400/35' : '',
              )}
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <span className="w-16 tabular-nums text-[11px] text-[var(--text-muted)]">
                {formatSessionOffset(action.timestamp)}
              </span>
              <KindIcon kind={action.kind} />
              <span
                className={cn(
                  'flex-1 truncate',
                  action.kind === 'error' ? 'font-medium text-[var(--error)]' : '',
                )}
              >
                {action.summary}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
